import CoreGeneralQuestionUI, { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from '../Components/ui/react/core-general-question-ui';
import GameManager from '../GameManager';
import { coreConstantsGeneralUIs } from '../Misc/CoreConstants';
import UIManager, { UI, UIInstance } from './UiManager';
import React from 'react';
import { SingleUI } from '../Components/ui/react/base/SingleUI';
import CoreGeneralFeedbackUI, { CoreGeneralFeedbackUIConfig } from '../Components/ui/react/core-general-feedback-ui';
import CoreGeneralAARUI, { GeneralAARUIConfig } from '../Components/ui/react/core-general-aar-ui';
import CoreLoadingUI, { CoreLoadingUIConfig } from '../Components/ui/react/core-loading-ui';

/**
 * Manages the UI for a JSX-type framework
 */
export default class JsxUIManager extends UIManager {
	/**
	 * ctor
	 * @param gameManager A reference to the Game Manager
	 */
	constructor(gameManager: GameManager) {
		super(gameManager)
	}

	/**
	 * Registers a single UI
	 * @param uiName The name of the UI to register
	 * @param uiData Data to append to the UI
	 * @param tag Optional: Use this to group UIs. Example: 'HUD'
	 * @returns true if it was successfully registered
	 */
	public registerUI(uiName: string, uiData: JSXUIData, tag?: string): boolean {
		let result = super.registerUI(uiName, uiData, tag);
		return result;
	}

	/**
	 * Register all general uis to use for the game
	 */
	public registerGeneralUIs(): void {
		this.registerUI(coreConstantsGeneralUIs.CoreGeneralQuestion, {
			renderMethod: (instance: any) => {
				return <CoreGeneralQuestionUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralQuestionUI>
			}
		});
		this.registerUI(coreConstantsGeneralUIs.CoreGeneralFeedback, {
			renderMethod: (instance: any) => {
				return <CoreGeneralFeedbackUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralFeedbackUI>
			}
		});

		this.registerUI(coreConstantsGeneralUIs.CoreGeneralAAR, {
			debugUITemplate: null,
			renderMethod: (instance: any) => {
				return <CoreGeneralAARUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralAARUI>
			}
		});

		this.registerUI(coreConstantsGeneralUIs.CoreLoadingUI, {
			debugUITemplate: null,
			renderMethod: (instance: any) => {
				return <CoreLoadingUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreLoadingUI>
			}
		});
	}

	/**
	 * Renders all single UIs
	 */
	public renderSingleGeneralUIs(): any {
		return (
			<div>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralQuestion} uiManager={this}></SingleUI>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralFeedback} uiManager={this}></SingleUI>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralAAR} uiManager={this}></SingleUI>
				<SingleUI id={coreConstantsGeneralUIs.CoreLoadingUI} uiManager={this}></SingleUI>
			</div>
		)
	}

	/**
	 * Calls renderMethod in all UIs.
	 * @returns The html to render
	 */
	public render(): any {
		let result = [];
		for (let uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			let ui = this._uis[uiIndx];

			for (let uiInstanceIndx = 0; uiInstanceIndx < ui.instances.length; uiInstanceIndx++) {
				let instance = ui.instances[uiInstanceIndx];
				let res = this._renderUiInstance(ui, instance);
				if (res) {
					result.push(res);
				}
			}
		}

		return result;
	}

	/**
	 * Renders a single instance (if it should be rendered)
	 * @param ui The instance of the UI
	 * @param instance The instance itself
	 * @returns The html to render for a single instance
	 */
	private _renderUiInstance(ui: UI, instance: UIInstance): any {
		return instance.parameters.isVisible && ui.uiData.renderMethod ?
			ui.uiData.renderMethod(instance)
			: null;
	}
}

export interface JSXUIData {
	/**
	 * A sample configuration of what the UI will have. Only used for debug purposes
	 */
	debugUITemplate?: JSXUIDebugTemplate[];

	/**
	 * Called for every instance of this UI when it needs to be rendered
	 */
	renderMethod: (uiInstance: any) => any;
}

export interface JSXUIDebugTemplate {
	name: string;
	debugUITemplate: () => any;
}